// frontend/src/pages/public/QrTypesPage.jsx
import React from "react";
import {
  LinkIcon,
  WifiIcon,
  IdentificationIcon,
  EnvelopeIcon,
  PhoneIcon,
  DocumentTextIcon,
  DocumentIcon,
  VideoCameraIcon,
  QueueListIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  MapPinIcon,
  CalendarIcon,
  TicketIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import Seo from "../../marketing/Seo";
import { Container, Section, Eyebrow, Button } from "../../marketing/ui";
import { CTASection } from "../../marketing/blocks";

// Mirrors the supported qr_type discriminators in components/QrPayloadFields.jsx.
const TYPES = [
  { icon: LinkIcon, name: "URL", desc: "Forward to any web page. Dynamic, so you can change the destination any time — the most flexible type." },
  { icon: WifiIcon, name: "WiFi", desc: "Let people join a network by scanning — no typing the password." },
  { icon: IdentificationIcon, name: "vCard", desc: "Share contact details that save straight to the scanner's phone." },
  { icon: EnvelopeIcon, name: "Email", desc: "Pre-fill a new email with the recipient, subject, and body." },
  { icon: PhoneIcon, name: "Phone", desc: "Start a call to a number with one scan." },
  { icon: DocumentTextIcon, name: "Plain Text", desc: "Show a short message or note directly on scan." },
  { icon: DocumentIcon, name: "PDF", desc: "Link straight to a PDF — a menu, spec sheet, or brochure." },
  { icon: VideoCameraIcon, name: "Video", desc: "Send scanners to a product demo, tutorial, or promo video." },
  { icon: QueueListIcon, name: "Menu", desc: "Point to your restaurant or venue's menu page." },
  { icon: ChatBubbleLeftRightIcon, name: "Feedback form", desc: "Collect reviews or feedback right after a visit or purchase." },
  { icon: DevicePhoneMobileIcon, name: "App download", desc: "Send scanners straight to your app's download page." },
  { icon: MapPinIcon, name: "Location", desc: "Drop a pin or address that opens directly in Maps." },
  { icon: CalendarIcon, name: "Event", desc: "Share an event's title, time, and location in one scan." },
  { icon: TicketIcon, name: "Coupon", desc: "Hand out a discount code or offer at checkout or in print." },
  { icon: ShareIcon, name: "Social links", desc: "One code linking out to all your social profiles." },
];

export default function QrTypesPage() {
  return (
    <>
      <Seo
        title="QR code types"
        description="QRFlow supports 15 QR code types — URL, WiFi, vCard, Email, Phone, Plain Text, PDF, Video, Menu, Feedback form, App download, Location, Event, Coupon, and Social links."
        path="/qr-types"
      />

      <div className="bg-gradient-to-b from-brand-50/60 to-white">
        <Container className="py-16 text-center sm:py-20">
          <Eyebrow>QR code types</Eyebrow>
          <h1 className="mx-auto mt-2 max-w-3xl text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            One platform, every common code type
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-600">
            Pick the type that fits, then track every scan. Need something not listed here?
            Use a dynamic <strong>URL</strong> code — it can point at anything and you can
            change it later.
          </p>
        </Container>
      </div>

      <Section>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {TYPES.map((t) => (
            <div key={t.name} className="rounded-2xl bg-white p-6 ring-1 ring-gray-200">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <t.icon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">{t.name}</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">{t.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Button to="/register" size="lg">Create your first code free</Button>
        </div>
      </Section>

      <CTASection
        title="Every type is dynamic and tracked"
        subtitle="Change destinations without reprinting and watch scans land in real time."
        primary={{ to: "/register", label: "Get started free" }}
        secondary={{ to: "/docs/getting-started", label: "Read the guide" }}
      />
    </>
  );
}
